import type { ExperienceListFilters } from 'actions/ExperienceActions'
import { fetchExperienceList } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { getExperienceListKey } from 'lib/experiences/experienceUtils'
import { split } from 'lib/array/arrayUtils'
import { useEffect, useMemo } from 'react'

type Filters = Omit<ExperienceListFilters, 'experienceIds'>

const BATCH_SIZE = 50

/**
 * Like useExperiences but splits large ID lists into batched requests
 * to avoid URL length limits on GET requests.
 */
function useExperiencesBatched(experienceIds: Array<string> = [], filters: Filters = {}): [Array<App.ExperienceOffer>, boolean] {
  const dispatch = useAppDispatch()
  const stableExperienceIds = useStableObjectReference(experienceIds)

  const batches = useMemo(() => split(stableExperienceIds, BATCH_SIZE), [stableExperienceIds])

  const keys = useMemo(
    () => batches.map(batch => getExperienceListKey({ ...filters, experienceIds: batch })),
    [batches, filters],
  )

  useEffect(() => {
    for (const batch of batches) {
      if (batch.length) {
        dispatch(fetchExperienceList({ ...filters, experienceIds: batch }))
      }
    }
  }, [batches, filters, dispatch])

  const experience = useAppSelector(state => state.experience.experiences)
  const experienceLists = useAppSelector(state => state.experience.experienceLists)

  const loading = stableExperienceIds.length > 0 && keys.some(key => {
    const listState = experienceLists[key]
    return !listState || listState.fetching
  })

  const experiences = useMemo(
    () => stableExperienceIds.map(id => experience[id]).filter(Boolean),
    [experience, stableExperienceIds],
  )

  return [experiences, loading]
}

export default useExperiencesBatched
